import { useEffect, useState } from 'react';
import { DayPickerRangeController, DateRangePicker } from 'react-dates';
import { styled } from '@mui/system';
import 'react-dates/initialize';
import PropTypes from 'prop-types';
import momentPropTypes from 'react-moment-proptypes';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { themeColors } from '../../../utils/theme';

const VerticalDateRangePicker = (props) => {
    const { dateFormat, inputWidth, hasInput, onDateRangeChange, isOutsideRange } = props;
    const [start, setStart] = useState(props.startDate);
    const [end, setEnd] = useState(props.endDate);
    const [focused, setFocused] = useState(null);

    useEffect(() => {
        setStart(props.startDate);
        setEnd(props.endDate);
    }, []);

    useEffect(() => {
        if (!hasInput) {
            setFocused('startDate');
        }
    }, [end]);

    useEffect(() => {
        if (!hasInput) {
            setFocused('endDate');
        }
    }, [start]);

    const onDatesChange = (startDate, endDate) => {
        if (onDateRangeChange) {
            onDateRangeChange(startDate, endDate);
        }
        setStart(startDate);
        setEnd(endDate);
    };

    if (!hasInput) {
        return (
            <StyledDatePickerWrapper id="date_range_picker" inputwidth={inputWidth}>
                <DayPickerRangeController
                    startDate={start}
                    endDate={end}
                    orientation="vertical"
                    verticalHeight={568}
                    onDatesChange={({ startDate, endDate }) => onDatesChange(startDate, endDate)}
                    numberOfMonths={2}
                    focusedInput={focused}
                    onFocusChange={(focusedInput) => setFocused(focusedInput)}
                    hideKeyboardShortcutsPanel
                    noBorder
                    enableOutsideDays={false}
                    isOutsideRange={isOutsideRange}
                    navPrev={<StyledChevronLeftRoundedIcon id="prev_arrow" fontSize="small" />}
                    navNext={<StyledChevronRightRoundedIcon id="next_arrow" fontSize="small" />}
                />
            </StyledDatePickerWrapper>
        );
    }

    return (
        <DateRangeWithInput focused={focused} id="date_range_picker">
            <DateRangePicker
                startDate={start}
                startDatePlaceholderText="Start"
                endDatePlaceholderText="End"
                startDateId="start_date_id"
                endDate={end}
                endDateId="end_date_id"
                orientation="vertical"
                verticalHeight={580}
                onDatesChange={({ startDate, endDate }) => onDatesChange(startDate, endDate)}
                focusedInput={focused}
                onFocusChange={(focusedInput) => setFocused(focusedInput)}
                hideKeyboardShortcutsPanel
                displayFormat={dateFormat}
                small
                block
                anchorDirection="left"
                enableOutsideDays={false}
                isOutsideRange={isOutsideRange}
                navPrev={<StyledChevronLeftRoundedIcon id="prev_arrow" fontSize="small" />}
                navNext={<StyledChevronRightRoundedIcon id="next_arrow" fontSize="small" />}
            />
        </DateRangeWithInput>
    );
};

export default VerticalDateRangePicker;

VerticalDateRangePicker.propTypes = {
    startDate: momentPropTypes.momentObj,
    endDate: momentPropTypes.momentObj,
    dateFormat: PropTypes.string,
    inputWidth: PropTypes.number,
    hasInput: PropTypes.bool,
    isOutsideRange: PropTypes.bool,

    onDateRangeChange: PropTypes.func,
};

const StyledChevronLeftRoundedIcon = styled(ChevronLeftRoundedIcon)(({ theme }) => ({
    position: 'absolute',
    right: '15%',
    top: 22,
    color: themeColors.calendarNav,
}));

const StyledChevronRightRoundedIcon = styled(ChevronRightRoundedIcon)(({ theme }) => ({
    position: 'absolute',
    right: '5%',
    top: 22,
    color: themeColors.calendarNav,
}));

const StyledDatePickerWrapper = styled('div')(({ theme, inputwidth }) => ({
    width: '100%',
    '.CalendarDay': {
        border: 'none',
        color: themeColors.calendarBlack,
    },
    '.CalendarMonth_caption': {
        alignItems: 'flex-start',
        textAlign: 'left',
        strong: {
            fontFamily: 'Nunito Sans',
            fontStyle: 'normal',
            fontWeight: 'bold',
            fontSize: 16,
            lineHeight: '140%',
            color: '#434d68',
            textAlign: 'left',
            paddingLeft: 13,
        },
    },
    '.CalendarDay__blocked_out_of_range, .CalendarDay__blocked_out_of_range:active, .CalendarDay__blocked_out_of_range:hover': {
        color: themeColors.calendarGrey,
    },
    '.CalendarDay__selected_start': {
        width: 34,
        height: 34,
        background: themeColors.primaryLight,
        border: 0,
        padding: 0,
        borderRadius: '50%',
        position: 'relative',
    },
    '.CalendarDay__selected_start::before': {
        content: "''",
        display: 'inline-block',
        background: themeColors.primaryLight,
        opacity: 0.5,
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
    },
    '.CalendarDay__selected_end': {
        width: 34,
        height: 34,
        background: themeColors.primaryLight,
        border: 0,
        padding: 0,
        borderRadius: '50%',
        position: 'relative',
    },
    '.CalendarDay__selected_end::before': {
        content: "''",
        display: 'inline-block',
        background: themeColors.primaryLight,
        opacity: 0.5,
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        right: 0,
    },
    '.CalendarDay__selected_span': {
        background: themeColors.primaryLight,
        opacity: 0.5,
        color: themeColors.calendarBlack,
    },
    '.CalendarDay__selected': {
        color: 'white',
        opacity: 1,
    },
    '.CalendarDay__selected:hover': {
        background: themeColors.primaryLight,
        color: themeColors.calendarBlack,
    },
    '.CalendarDay__hovered_span:hover, .CalendarDay__hovered_span': {
        background: themeColors.primaryLight,
        opacity: 0.5,
        border: 'none',
    },
    '.DateRangePickerInput__withBorder': {
        border: 'none',
    },
    '.DateInput': {
        width: inputwidth ? `${inputwidth}px` : '55px',
        textAlign: 'left',
    },
    '.DateInput_input, .DateInput_input_1': {
        fontSize: 15,
        padding: 0,
        display: 'flex',
        color: themeColors.placeholderSearchText,
        fontStyle: 'normal',
        fontWeight: 'normal',
        lineHeight: '24px',
    },
    '.DateInput_input__focused, .DateInput_input__focused_2': {
        borderBottom: `2px solid ${themeColors.primaryLight}`,
    },
    '.DateRangePickerInput_calendarIcon': {
        margin: 0,
        padding: 0,
        outline: 'none',
        boxShadow: 'none',
        paddingRight: 5,
    },
    '.DateInput_fang': {
        display: 'none',
    },
    '.DateRangePickerInput_calendarIcon_svg': {
        fill: themeColors.primaryLight,
        height: 15,
        width: 14,
        verticalAlign: 'middle',
        marginBottom: 5,
    },
    '.DateRangePickerInput_arrow': {
        paddingRight: 5,
        color: themeColors.placeholderSearchText,
    },
    '.DateRangePicker_picker': {
        background: 'transparent',
        position: 'inherit',
    },
}));

const DateRangeWithInput = styled('div')(({ theme, focused }) => ({
    width: '100%',
    '.CalendarDay': {
        border: 'none',
        color: themeColors.calendarBlack,
    },
    '.CalendarMonth_caption': {
        alignItems: 'flex-start',
        textAlign: 'left',
        strong: {
            fontFamily: 'Nunito Sans',
            fontStyle: 'normal',
            fontWeight: 'bold',
            fontSize: 16,
            lineHeight: '140%',
            color: '#434d68',
            textAlign: 'left',
            paddingLeft: 13,
        },
    },
    '.CalendarDay__blocked_out_of_range, .CalendarDay__blocked_out_of_range:active, .CalendarDay__blocked_out_of_range:hover': {
        color: themeColors.calendarGrey,
    },
    '.CalendarDay__selected_start': {
        width: 34,
        height: 34,
        background: themeColors.primaryLight,
        border: 0,
        padding: 0,
        borderRadius: '50%',
        position: 'relative',
    },
    '.CalendarDay__selected_start::before': {
        content: "''",
        display: 'inline-block',
        background: themeColors.primaryLight,
        opacity: 0.5,
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
    },
    '.CalendarDay__selected_end': {
        width: 34,
        height: 34,
        background: themeColors.primaryLight,
        border: 0,
        padding: 0,
        borderRadius: '50%',
        position: 'relative',
    },
    '.CalendarDay__selected_end::before': {
        content: "''",
        display: 'inline-block',
        background: themeColors.primaryLight,
        opacity: 0.5,
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        right: 0,
    },
    '.CalendarDay__selected_span': {
        background: themeColors.primaryLight,
        opacity: 0.5,
        color: themeColors.calendarBlack,
    },
    '.CalendarDay__selected': {
        color: 'white',
        opacity: 1,
    },
    '.CalendarDay__selected:hover': {
        background: themeColors.primaryLight,
        color: themeColors.calendarBlack,
    },
    '.CalendarDay__hovered_span:hover, .CalendarDay__hovered_span': {
        background: themeColors.primaryLight,
        opacity: 0.5,
        border: 'none',
    },
    '.DateInput_fang': {
        display: 'none',
    },
    '.DateInput_input': {
        border: '1px solid #d2d3d4',
        borderRadius: 2,
        boxSizing: 'border-box',
        textAlign: 'center',
        fontSize: 16,
    },
    '.DateInput_input__focused': {
        background: '#f7f7f7',
        border: '1px solid #f7f7f7',
    },
    '.DayPicker__withBorder': {
        boxShadow: '0 0 52px rgba(0, 0, 0, 0.04)',
        borderRadius: 10,
        border: 'none',
    },
    '.DateRangePickerInput__withBorder': {
        border: 'none',
    },
    '.DateRangePickerInput_arrow': {
        marginLeft: 10,
        marginRight: 10,
    },
    '.DateRangePicker__picker': {
        zIndex: 20,
        pointerEvents: 'all',
        paddingBottom: focused ? 430 : 0,
    },
}));
